import React, {useEffect, useState} from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";

import Badge from "../ui/badge/Badge";
import { User } from "../../types";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { addUserRequest, fetchUsersStart } from "../../slices/userSlice";
import { useNavigate } from "react-router";
import {useAppSelector} from "../../store/hook";
import {apiRequestWithAuth} from "../../utils/api";
import MultiSelect from "../form/MultiSelect";
import MultiSelect2 from "../form/MultiSelect2";
import Select from "react-select";
import {useSearchParams} from "react-router-dom";
import {AddIcon, EditIcon, ListIcon, MenuAlt, PlusIcon} from "../../icons";

interface Course {
    id: number;
    title: string;
    shortDescription: string;
    durationDays: string;
    categoryId: string;
    category: {
        id: string;
        title: string;
    },
    quizzes: any[];
    showImage: {
        publicPath: string;
    },
    createTime: string;
    status: string;
    statusText: string;
    visibility: string;
}

export default function CourseListComponent() {

    const [searchParams, setSearchParams] = useSearchParams();
    const [pageId, setPage] = useState(() => {
        return parseInt(searchParams.get('page') || '1', 10);
    });
    const [hasResult, setHasResult] = useState(true);

    const [courses, setCourses] = useState<Course[]>([]);
    const navigate = useNavigate();
    const token = useAppSelector((state) => state.auth.token);
    const [loading, setLoading] = useState(true);
    const [openExamPopup, setOpenExamPopup] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
    const [examOptions, setExamOptions] = useState<{ label: string; value: string }[]>([]);
    const [selectedExamId, setSelectedExamId] = useState<any[]>([]);
    const [selectedValues, setSelectedValues] = useState<any[]>([]);
    const [courseSelected, setCourseSelected] = useState<Course>();

    // Filter states
    const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
    const [categoryId, setCategoryId] = useState(searchParams.get('categoryId') || '');
    const [status, setStatus] = useState(searchParams.get('status') || '');
    const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        fetchCategories();
        fetchExams();
    }, []);

    useEffect(() => {
        fetchCourses();
    }, [pageId]);

    const handlePageChange = (newPage: React.SetStateAction<number>) => {
        setPage(newPage);
        setSearchParams({ page: newPage.toString() });
    };

    const buildQuery = (page: number) => {
        const params: Record<string, string> = { page: page.toString() };
        if (keyword) params.keyword = keyword;
        if (categoryId) params.categoryId = categoryId;
        if (status) params.status = status;
        return new URLSearchParams(params).toString();
    };

    const fetchCategories = async () => {
        try {
            const res = await apiRequestWithAuth('/category', 'GET', {});
            if (res.messageCode === 200) {
                const rawCategories = res.result || [];

                // Gom nhóm theo parentId (giống ExamsListBuilder)
                const mapByParent: Record<string, any[]> = {};
                rawCategories.forEach((cat: any) => {
                    const pid = cat.parentId ?? "root";
                    if (!mapByParent[pid]) mapByParent[pid] = [];
                    mapByParent[pid].push(cat);
                });

                // Build tree phẳng + prefix đệ quy
                const buildHierarchy = (parentId: string = "root", prefix: string = ""): { value: string; label: string }[] => {
                    const children = mapByParent[parentId] || [];
                    return children.flatMap((child: any) => [
                        { value: child.id.toString(), label: prefix + child.title },
                        ...buildHierarchy(child.id.toString(), prefix + "-- "),
                    ]);
                };

                setCategories(buildHierarchy());
            }
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const fetchCourses = async () => {
        try {
            const response = await apiRequestWithAuth("/course?" + buildQuery(pageId), "GET", {});
            if (response.messageCode === 200) {
                setCourses(response.result);
                if (response.result.length === 20) {
                    setHasResult(true);
                } else {
                    setHasResult(false);
                }
            } else {
                setCourses([]);
                setHasResult(false);
            }
            setLoading(false);
        } catch (err) {
            console.error("Error fetching courses:", err);
        }
    };

    const handleSearch = () => {
        const params: Record<string, string> = { page: '1' };
        if (keyword) params.keyword = keyword;
        if (categoryId) params.categoryId = categoryId;
        if (status) params.status = status;
        setSearchParams(params);
        setPage(1);
        fetchCourses();
    };

    const handleReset = () => {
        setKeyword('');
        setCategoryId('');
        setStatus('');
        setSearchParams({ page: '1' });
        setPage(1);
        setTimeout(fetchCourses, 0);
    };

    const addCourse = () => {
        navigate("/create-course");
    };

    const handleAddLesson = (courseId: number) => {
        navigate(`/course/${courseId}/lesson/create`);
    };

    const fetchExams = async () => {
        try {
            const res = await apiRequestWithAuth("/quiz?limit=100", "GET", {});
            setExamOptions(
                res.result.map((exam: any) => ({
                    label: exam.title,
                    value: exam.id
                }))
            );
        } catch (error) {
            console.error("Failed to load exams", error);
        }
    };

    const handleOpenExamPopup = (courseId: string) => {
        setSelectedCourseId(courseId);
        setOpenExamPopup(true);
    };

    const handleAssignExam = async () => {
        if (!selectedCourseId || !selectedExamId) return;

        try {
            const doUpdate = await apiRequestWithAuth(`/course/${selectedCourseId}`, "PUT", {
                quizzes: selectedExamId,
            });
            if (doUpdate.messageCode == 200) {
                setCourses((prevCourses) => {
                    return prevCourses.map((course) => {
                        if (course.id.toString() === selectedCourseId) {
                            return { ...course, quizzes: doUpdate.result.quizzes || []};
                        }
                        return course;
                    });
                });
                alert("Thêm bài thi thành công!");
                setOpenExamPopup(false);
            } else {
                alert("Thêm bài thi thất bại: " + doUpdate.message);
            }
        } catch (error) {
            console.error("Failed to assign exam", error);
        }
    };

    if (loading) return <p>Loading courses...</p>;
    // if (error) return <p>Error: {error}</p>;

    return (
        <>
            {/* Action button */}
            <button
                className="mb-4 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                onClick={addCourse}
            >
                + Thêm khóa học
            </button>

            {/* Filter Bar */}
            <div className="mb-4 p-4 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] rounded-xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                    {/* Keyword */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Từ khóa</label>
                        <input
                            type="text"
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            placeholder="Tìm kiếm..."
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Danh mục</label>
                        <select
                            value={categoryId}
                            onChange={e => setCategoryId(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        >
                            <option value="">Tất cả danh mục</option>
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Trạng thái</label>
                        <select
                            value={status}
                            onChange={e => setStatus(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        >
                            <option value="">Tất cả</option>
                            <option value="1">Active</option>
                            <option value="2">Draft</option>
                            <option value="3">Inactive</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={handleSearch}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition"
                    >
                        Tìm kiếm
                    </button>
                    <button
                        onClick={handleReset}
                        className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium py-2 px-4 rounded-lg transition"
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div
                className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                <div className="max-w-full overflow-x-auto">
                    <div className="min-w-[1102px]">
                        <Table>
                            {/* Table Header */}
                            <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                <TableRow>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        ID
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Hình ảnh
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Tên khóa học
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Danh mục
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Thời hạn (ngày)
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Trạng thái
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Visibility
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                    >
                                        Ngày tạo
                                    </TableCell>
                                    <TableCell isHeader
                                               className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 text-nowrap">Lesson</TableCell>
                                    <TableCell isHeader
                                               className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 text-nowrap">Quiz</TableCell>
                                    <TableCell isHeader
                                               className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 text-nowrap">Hành
                                        động</TableCell>
                                </TableRow>
                            </TableHeader>


                            {/* Table Body */}
                            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                {courses.map((course) => (
                                    <TableRow key={course.id}>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {course.id}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            <img style={{maxWidth: '120px', maxHeight: '90px'}}
                                                 src={course.showImage?.publicPath} alt=""/>
                                        </TableCell>
                                        <TableCell className="px-5 py-4 sm:px-6 text-start">
                                            <div className="flex items-center gap-3">
                                                <div>
                                                  <span
                                                      className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                    {course.title}
                                                  </span>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            {course?.category?.title || 'Chưa có danh mục'}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            {course.durationDays}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium text-theme-xs
                                                ${course.statusText == 'Active' ? 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500' : 'bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500'}`}>
                                              {course.statusText}
                                            </span>
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium text-theme-xs
                                                ${course.visibility == 'public' ? 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500' : 'bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500'}`}>
                                              {course.visibility}
                                            </span>
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {course.createTime}
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-end text-nowrap">
                                            <button
                                                className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm py-1 px-1 rounded-lg mr-2"
                                                onClick={() => handleAddLesson(course.id)}
                                            >
                                                <AddIcon />
                                            </button>
                                            <button
                                                className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm text-nowrap py-1 px-1 rounded-lg"
                                                onClick={() => navigate(`/course/${course.id}/lesson`)}
                                            >
                                                <MenuAlt />
                                            </button>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-center">
                                            <button
                                                className="bg-indigo-500 hover:bg-indigo-600 text-white text-nowrap text-sm py-1 px-1 rounded-lg"
                                                onClick={() => {
                                                    handleOpenExamPopup(course.id.toString())
                                                    setCourseSelected(course)
                                                }}
                                            >
                                                <AddIcon />
                                            </button>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-end">
                                            <button onClick={() => navigate(`/course/${course.id}/update`)}
                                                    className="text-sm px-1.5 py-1.5 rounded-md bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition">
                                                <EditIcon />
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <div className="p-4 border-t border-gray-100 dark:border-gray-800 sm:p-6">
                            <div className="space-y-6">
                                <div className="flex items-center justify-between gap-8 sm:justify-between">
                                    <button
                                        disabled={pageId === 1}
                                        onClick={() => handlePageChange(pageId - 1)}
                                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 sm:px-3.5 sm:py-2.5"
                                    >
                                        <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20"
                                             fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd"
                                                  d="M2.58203 9.99868C2.58174 10.1909 2.6549 10.3833 2.80152 10.53L7.79818 15.5301C8.09097 15.8231 8.56584 15.8233 8.85883 15.5305C9.15183 15.2377 9.152 14.7629 8.85921 14.4699L5.13911 10.7472L16.6665 10.7472C17.0807 10.7472 17.4165 10.4114 17.4165 9.99715C17.4165 9.58294 17.0807 9.24715 16.6665 9.24715L5.14456 9.24715L8.85919 5.53016C9.15199 5.23717 9.15184 4.7623 8.85885 4.4695C8.56587 4.1767 8.09099 4.17685 7.79819 4.46984L2.84069 9.43049C2.68224 9.568 2.58203 9.77087 2.58203 9.99715C2.58203 9.99766 2.58203 9.99817 2.58203 9.99868Z"
                                                  fill=""></path>
                                        </svg>
                                        <span className="hidden sm:inline"> Previous </span>
                                    </button>
                                    <button
                                        disabled={!hasResult}
                                        onClick={() => handlePageChange(pageId + 1)}
                                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 sm:px-3.5 sm:py-2.5">
                                        <span className="hidden sm:inline"> Next </span>
                                        <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20"
                                             fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd"
                                                  d="M17.4165 9.9986C17.4168 10.1909 17.3437 10.3832 17.197 10.53L12.2004 15.5301C11.9076 15.8231 11.4327 15.8233 11.1397 15.5305C10.8467 15.2377 10.8465 14.7629 11.1393 14.4699L14.8594 10.7472L3.33203 10.7472C2.91782 10.7472 2.58203 10.4114 2.58203 9.99715C2.58203 9.58294 2.91782 9.24715 3.33203 9.24715L14.854 9.24715L11.1393 5.53016C10.8465 5.23717 10.8467 4.7623 11.1397 4.4695C11.4327 4.1767 11.9075 4.17685 12.2003 4.46984L17.1578 9.43049C17.3163 9.568 17.4165 9.77087 17.4165 9.99715C17.4165 9.99763 17.4165 9.99812 17.4165 9.9986Z"
                                                  fill=""></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {openExamPopup && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-99999">
                            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
                                <h2 className="text-xl font-semibold mb-4">Chọn bài thi</h2>

                                {/* Multi Select */}
                                <div>
                                    <MultiSelect2
                                        options={examOptions}
                                        defaultSelected={examOptions.filter((option) => {
                                            return courseSelected?.quizzes?.map((quiz) => quiz.id.toString()).includes(option.value.toString());
                                        })}
                                        onChange={(selectedOptions) => {
                                            const selectedValues = selectedOptions.map(opt => opt.value);
                                            setSelectedExamId(selectedValues)
                                        }}
                                    />
                                </div>

                                <div className="flex justify-end gap-3">
                                    <button
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                                        onClick={() => setOpenExamPopup(false)}
                                    >
                                        Hủy
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                        onClick={handleAssignExam}
                                    >
                                        Lưu
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
